import React, { createContext, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import useResearchTableActions from '@/finsight/application/actions/research/researchTable/actions';
import {
  IResearchData,
} from '@/finsight/domain/vo/research/IResearchData';
import {
  IResearchContextData,
  IResearchContextDataActions,
} from '@/finsight/domain/vo/research/IResearchContextData';
import { IResearchSSRResponse } from '@/finsight/domain/vo/research/IResearchResponse';
import { initialResearchState } from '@/finsight/application/Research/constants';
import {
  filterResearchResponse,
} from '@/finsight/application/Research/serverSide/helpers';
import { DEFAULT } from '@/finsight/domain/DocumentGroup';
import { makeEmptyGroupIds } from '@/finsight/application/actions/research/helpers';
import { useDocumentGroupContext } from '@/finsight/application/Research/DocumentGroupContext';

const useResearchData = ({
  response,
  isTile,
  includedDocumentGroup,
  preparedRequestPayload,
  isHideModule,
}: IResearchSSRResponse) => {
  const filteredResponse = filterResearchResponse(response, includedDocumentGroup.groupId);
  const [researchData, setResearchData] = useState<IResearchData>(
      { ...initialResearchState(isTile), ...filteredResponse });
  const [requestPayload, setRequestPayload] = useState(preparedRequestPayload);
  const { query: { q = '', products, regions } } = useRouter();
  const [searchFieldValue, setSearchFieldValue] = useState(q);
  const { setEmptyDocumentGroupIds, setDocumentGroup } = useDocumentGroupContext();

  const actions: IResearchContextDataActions = useResearchTableActions(
      researchData,
      setResearchData,
      requestPayload,
      searchFieldValue,
  );

  useEffect(() => {
    actions.paginate(1);
  }, [products, regions]);

  useEffect(() => setRequestPayload(preparedRequestPayload), [preparedRequestPayload]);

  useEffect(() => {
    const data = { ...initialResearchState(isTile), ...filteredResponse } as IResearchData;
    const emptyGroupIds = makeEmptyGroupIds(response);

    setEmptyDocumentGroupIds(emptyGroupIds);
    setDocumentGroup(emptyGroupIds.includes(includedDocumentGroup.groupId) ? DEFAULT : includedDocumentGroup);

    setResearchData(data);
  }, [response, includedDocumentGroup]);

  return {
    researchData,
    searchFieldValue,
    setSearchFieldValue,
    isHideModule,
    ...actions,
  };
};

const ResearchContext = createContext<IResearchContextData>(null);

export const useResearchContext = () => {
  const context: IResearchContextData = useContext(ResearchContext);
  if (!context) {
    throw new Error('useResearchContext must be used within the ResearchContext');
  }
  return context;
};

const ResearchContextProvider = ({ children, data }) => (
  <ResearchContext.Provider value={ useResearchData(data) }>
    { children }
  </ResearchContext.Provider>
);

export default ResearchContextProvider;
